import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { CalendarClock, Wrench, History, Info, Settings } from 'lucide-react';

const VacacionesSaldosCard = () => {
  const [saldos, setSaldos] = useState([]);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [cargando, setCargando] = useState(false);
  const [dlg, setDlg] = useState({ open: false, saldo: null, delta: '', motivo: '' });
  const [hist, setHist] = useState({ open: false, user_id: null, nombre: '', items: [] });
  const [polDlg, setPolDlg] = useState({ open: false, dias_anio: 30, tipo_computo: 'naturales', traspaso_permitido: false, traspaso_fecha_limite: '03-31', extremos_estricto: false });

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await api.get(`/admin/vacaciones/saldos?anio=${anio}`);
      setSaldos(r.data.saldos || []);
    } catch { toast.error('No se pudo cargar saldos'); }
    finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, [anio]);

  const abrirAjuste = (s) => setDlg({ open: true, saldo: s, delta: '', motivo: '' });
  const cerrarAjuste = () => setDlg({ open: false, saldo: null, delta: '', motivo: '' });

  const guardarAjuste = async () => {
    const delta = parseFloat(dlg.delta);
    if (!delta || Number.isNaN(delta)) { toast.error('Introduce un delta distinto de 0'); return; }
    if (!dlg.motivo.trim()) { toast.error('El motivo es obligatorio'); return; }
    try {
      await api.post(`/admin/vacaciones/saldos/${dlg.saldo.empleado.id}/ajustar`,
        { anio, delta, motivo: dlg.motivo.trim() });
      toast.success('Saldo ajustado');
      cerrarAjuste();
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const abrirHistorial = async (s) => {
    try {
      const r = await api.get(`/admin/vacaciones/saldos/${s.empleado.id}/historial?anio=${anio}`);
      setHist({ open: true, user_id: s.empleado.id, nombre: s.empleado.full_name, items: r.data });
    } catch { toast.error('No se pudo cargar el historial'); }
  };

  const abrirPolitica = async () => {
    try {
      const r = await api.get('/admin/vacaciones/politica');
      setPolDlg({ open: true, ...r.data });
    } catch { toast.error('No se pudo cargar la política'); }
  };
  const guardarPolitica = async () => {
    try {
      await api.put('/admin/vacaciones/politica', {
        dias_anio: parseInt(polDlg.dias_anio, 10),
        tipo_computo: polDlg.tipo_computo,
        traspaso_permitido: !!polDlg.traspaso_permitido,
        traspaso_fecha_limite: polDlg.traspaso_fecha_limite,
        extremos_estricto: !!polDlg.extremos_estricto,
      });
      toast.success('Política actualizada');
      setPolDlg({ ...polDlg, open: false });
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <>
      <Card className="border-zinc-200" data-testid="vacaciones-saldos-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-4 flex-wrap">
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <CalendarClock className="h-5 w-5" /> Saldos de vacaciones
            <Badge variant="outline" className="ml-1" data-testid="saldos-count">{saldos.length}</Badge>
          </CardTitle>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={abrirPolitica} data-testid="abrir-politica-btn">
              <Settings className="h-3.5 w-3.5 mr-1" /> Política de empresa
            </Button>
            <Label className="text-xs">Año</Label>
            <Select value={String(anio)} onValueChange={(v) => setAnio(parseInt(v, 10))}>
              <SelectTrigger className="h-8 w-24" data-testid="saldos-anio"><SelectValue /></SelectTrigger>
              <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[26rem] overflow-y-auto">
            <table className="w-full text-sm" data-testid="saldos-table">
              <thead className="bg-zinc-50 sticky top-0">
                <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  <th className="py-2.5 px-4">Empleado</th>
                  <th className="py-2.5 px-4 text-right">Asignados</th>
                  <th className="py-2.5 px-4 text-right">Ajustes</th>
                  <th className="py-2.5 px-4 text-right">Consumidos</th>
                  <th className="py-2.5 px-4 text-right">Pendientes</th>
                  <th className="py-2.5 px-4 text-right">Disponibles</th>
                  <th className="py-2.5 px-4">Política</th>
                  <th className="py-2.5 px-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cargando && saldos.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-zinc-400">Cargando...</td></tr>
                )}
                {saldos.map((s) => (
                  <tr key={s.empleado.id} className="border-t border-zinc-100" data-testid={`saldo-row-${s.empleado.id}`}>
                    <td className="py-2.5 px-4 font-medium text-zinc-900">{s.empleado.full_name}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums">{s.asignados}</td>
                    <td className={`py-2.5 px-4 text-right tabular-nums ${s.ajuste > 0 ? 'text-emerald-700' : s.ajuste < 0 ? 'text-red-600' : 'text-zinc-400'}`}>
                      {s.ajuste > 0 ? '+' : ''}{s.ajuste}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums">{s.consumidos}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-amber-700">{s.pendientes}</td>
                    <td className={`py-2.5 px-4 text-right tabular-nums font-bold ${s.disponibles < 0 ? 'text-red-600' : 'text-zinc-900'}`}>
                      {s.disponibles}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-zinc-500">
                      {s.politica.dias_anio} · {s.politica.tipo_computo === 'naturales' ? 'naturales' : 'laborables'}
                      <div className="text-[10px] uppercase tracking-wider text-zinc-400">de {s.politica.fuente}</div>
                    </td>
                    <td className="py-2.5 px-4 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="h-8 mr-1" onClick={() => abrirAjuste(s)} data-testid={`ajustar-saldo-${s.empleado.id}`}>
                        <Wrench className="h-3.5 w-3.5 mr-1" /> Ajustar
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => abrirHistorial(s)} data-testid={`historial-saldo-${s.empleado.id}`}>
                        <History className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {!cargando && saldos.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-zinc-400">Sin empleados activos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 px-4 py-2 border-t border-zinc-100">
            <Info className="h-3.5 w-3.5" />
            El saldo se calcula automáticamente al aprobar/rechazar solicitudes de tipo Vacaciones. Los otros tipos no descuentan.
          </div>
        </CardContent>
      </Card>

      {/* Diálogo ajuste manual */}
      <Dialog open={dlg.open} onOpenChange={(o) => !o && cerrarAjuste()}>
        <DialogContent data-testid="dialogo-ajuste">
          <DialogHeader>
            <DialogTitle>Ajustar saldo · {dlg.saldo?.empleado?.full_name}</DialogTitle>
          </DialogHeader>
          {dlg.saldo && (
            <div className="space-y-3 text-sm">
              <div className="bg-zinc-50 rounded-lg p-3 space-y-1">
                <div><span className="text-zinc-500">Año:</span> <strong>{anio}</strong></div>
                <div><span className="text-zinc-500">Saldo actual:</span> <strong>{dlg.saldo.disponibles}</strong> días</div>
                <div className="text-xs text-zinc-500">
                  Asignados {dlg.saldo.asignados} + ajustes {dlg.saldo.ajuste} − consumidos {dlg.saldo.consumidos}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Delta (días) — positivo suma, negativo resta</Label>
                <Input type="number" step="0.5" value={dlg.delta} onChange={(e) => setDlg({ ...dlg, delta: e.target.value })}
                  placeholder="Ej. +2 o -1.5" data-testid="ajuste-delta" />
              </div>
              <div className="space-y-1.5">
                <Label>Motivo (obligatorio, queda auditado)</Label>
                <Textarea rows={3} value={dlg.motivo} onChange={(e) => setDlg({ ...dlg, motivo: e.target.value })}
                  placeholder="Ej. Compensación por horas extra 2025 / corrección error alta" data-testid="ajuste-motivo" />
              </div>
              <div className="text-xs text-zinc-500">
                Nuevo saldo estimado: <strong>{(dlg.saldo.disponibles + (parseFloat(dlg.delta) || 0)).toFixed(2)}</strong> días
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={cerrarAjuste}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={guardarAjuste} data-testid="ajuste-confirmar">Guardar ajuste</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo historial */}
      <Dialog open={hist.open} onOpenChange={(o) => !o && setHist({ ...hist, open: false })}>
        <DialogContent data-testid="dialogo-historial">
          <DialogHeader>
            <DialogTitle>Historial de ajustes · {hist.nombre}</DialogTitle>
          </DialogHeader>
          {hist.items.length === 0 ? (
            <div className="text-center text-zinc-400 py-4 text-sm">Sin ajustes registrados.</div>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-2">
              {hist.items.map((a) => (
                <div key={a.id} className="border border-zinc-200 rounded-lg p-3 text-sm" data-testid={`historial-item-${a.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-bold ${a.delta > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {a.delta > 0 ? '+' : ''}{a.delta} días
                    </span>
                    <span className="ml-auto text-xs text-zinc-500">{new Date(a.timestamp).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}</span>
                  </div>
                  <div className="text-zinc-700">{a.motivo}</div>
                  <div className="text-[11px] text-zinc-400 mt-1">
                    por <strong>{a.admin_username}</strong> · saldo {a.saldo_antes} → {a.saldo_despues}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Diálogo política empresa */}
      <Dialog open={polDlg.open} onOpenChange={(o) => !o && setPolDlg({ ...polDlg, open: false })}>
        <DialogContent data-testid="dialogo-politica">
          <DialogHeader><DialogTitle>Política de vacaciones · Empresa</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-zinc-500">
              Estos son los valores por defecto para toda la empresa. Los centros, departamentos y empleados pueden sobrescribirlos.
              La legislación española (art. 38 ET) establece un mínimo de 30 días naturales al año.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Días de vacaciones al año</Label>
                <Input type="number" min="0" max="365" value={polDlg.dias_anio}
                  onChange={(e) => setPolDlg({ ...polDlg, dias_anio: e.target.value })} data-testid="politica-dias" />
              </div>
              <div className="space-y-1">
                <Label>Tipo de cómputo</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
                  value={polDlg.tipo_computo}
                  onChange={(e) => setPolDlg({ ...polDlg, tipo_computo: e.target.value })}
                  data-testid="politica-tipo"
                >
                  <option value="naturales">Días naturales</option>
                  <option value="laborables">Días laborables</option>
                </select>
              </div>
            </div>
            <div className="border-t border-zinc-100 pt-3 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!polDlg.traspaso_permitido}
                  onChange={(e) => setPolDlg({ ...polDlg, traspaso_permitido: e.target.checked })}
                  data-testid="politica-traspaso" />
                Permitir traspasar días no disfrutados al año siguiente
              </label>
              {polDlg.traspaso_permitido && (
                <div className="space-y-1 ml-6">
                  <Label className="text-xs">Fecha límite para disfrutarlos (formato MM-DD)</Label>
                  <Input value={polDlg.traspaso_fecha_limite}
                    onChange={(e) => setPolDlg({ ...polDlg, traspaso_fecha_limite: e.target.value })}
                    placeholder="03-31" data-testid="politica-traspaso-limite" />
                  <p className="text-[11px] text-zinc-400">Convenio típico: 31 de marzo del año siguiente.</p>
                </div>
              )}
            </div>
            {polDlg.tipo_computo === 'naturales' && (
              <div className="border-t border-zinc-100 pt-3 space-y-1">
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" checked={!!polDlg.extremos_estricto}
                    onChange={(e) => setPolDlg({ ...polDlg, extremos_estricto: e.target.checked })}
                    data-testid="politica-extremos" className="mt-0.5" />
                  <span>
                    Aplicar interpretación jurisprudencial estricta (STS 2016/2020)
                    <span className="block text-[11px] text-zinc-500 mt-0.5">
                      Al activarlo: los festivos oficiales del centro que caen en día laborable dentro del periodo
                      NO se descuentan (STS 08/06/2016) y los sáb/dom que caen justo al inicio o final tampoco
                      (STS 21/09/2020). Recomendado solo si el empresario <strong>impone</strong> el periodo.
                      Por defecto está desactivado: cuenta todos los días naturales del periodo solicitado,
                      igual que A3, Sage, Zucchetti y la mayoría de nóminas españolas.
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPolDlg({ ...polDlg, open: false })}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={guardarPolitica} data-testid="politica-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VacacionesSaldosCard;
